import { produce } from 'immer';

import { queryClient } from '../../shared/providers/react-query-provider';
import {
    getArchivedDossierOverviewsQueryKey,
    getDossierOverviewsQueryKey,
} from '../dossier-overview/hooks';
import { Contract } from '../contract/types';

import { getDossierQueryKey } from './queries';
import {
    DossierId,
    Memo,
    SerializedDossier,
    SerializedDossiers,
    TaskDefinition,
    TaskNote,
} from './types';

// *Note: Most of the methods here update both the individual dossier fetch, and the dossier-overview fetch
// That is because the redux store was setup in a way that those two were merged, so to be on the safe side for now,
// until we migrate the mutation to react-query, it is done that way.

export const updateDossierInCache = (dossier: SerializedDossier) => {
    queryClient.setQueryData<SerializedDossier>(getDossierQueryKey(dossier.id), (cur) => {
        if (!cur) return cur;
        return {
            ...cur,
            dossier: {
                ...cur?.dossier,
                [dossier.id]: dossier.dossier[dossier.id],
            },
        };
    });

    queryClient.setQueryData<SerializedDossiers>(getDossierOverviewsQueryKey(), (cur) => ({
        ...cur,
        dossier: {
            ...cur?.dossier,
            [dossier.id]: dossier.dossier[dossier.id],
        },
    }));
};

export const deleteDossierInCache = (dossierId: DossierId) => {
    queryClient.setQueryData<SerializedDossier>(getDossierQueryKey(dossierId), undefined);

    queryClient.setQueryData<SerializedDossiers>(getDossierOverviewsQueryKey(), (cur) => {
        if (!cur) return cur;

        const dossierWithoutDeleted = Object.fromEntries(
            Object.entries(cur.dossier).filter(([, dossier]) => dossier.id !== dossierId),
        );

        return {
            ...cur,
            dossier: dossierWithoutDeleted,
        };
    });
};

export const markMessageAsReadInCache = (dossierId: DossierId) => {
    queryClient.setQueryData<SerializedDossier>(getDossierQueryKey(dossierId), (cur) => {
        if (!cur) return cur;
        return produce(cur, (draft) => {
            draft.dossier[dossierId].discussion.hasUnreadMessages = false;
        });
    });
    queryClient.setQueryData<SerializedDossiers>(getDossierOverviewsQueryKey(), (cur) => {
        if (!cur) return cur;
        return produce(cur, (draft) => {
            // FIXME [hotfix] dossier should be defined but some time this call is finished before cache setup
            if (draft.dossier[dossierId]) {
                draft.dossier[dossierId].discussion.hasUnreadMessages = false;
            }
        });
    });
};

export const markMessageAsNoResponseRequiredInCache = (
    dossierId: DossierId,
    { hasResponseRequired }: { hasResponseRequired: boolean },
) => {
    queryClient.setQueryData<SerializedDossier>(getDossierQueryKey(dossierId), (cur) => {
        if (!cur) return cur;
        return produce(cur, (draft) => {
            draft.dossier[dossierId].discussion.hasResponseRequired = hasResponseRequired;
        });
    });
    queryClient.setQueryData<SerializedDossiers>(getDossierOverviewsQueryKey(), (cur) => {
        if (!cur) return cur;
        return produce(cur, (draft) => {
            // FIXME [hotfix] dossier should be defined but some time this call is finished before cache setup
            if (draft.dossier[dossierId]) {
                draft.dossier[dossierId].discussion.hasResponseRequired = hasResponseRequired;
            }
        });
    });
};

export const updateMemoInCache = (dossierId: DossierId, memo: Memo) => {
    queryClient.setQueryData<SerializedDossier>(getDossierQueryKey(dossierId), (cur) => {
        if (!cur) return cur;
        return produce(cur, (draft) => {
            if (!draft.dossierMemo) {
                draft.dossierMemo = {};
            }
            draft.dossierMemo[dossierId] = memo;
        });
    });
};

export const updateContractInCache = (dossierId: DossierId, contract: Contract | null) => {
    queryClient.setQueryData<SerializedDossier>(getDossierQueryKey(dossierId), (cur) => {
        if (!cur) return cur;
        return produce(cur, (draft) => {
            draft.dossier[dossierId].contract = {
                state: contract ? 'issued' : 'not-issued',
            };
        });
    });
    queryClient.setQueryData<SerializedDossiers>(getDossierOverviewsQueryKey(), (cur) => {
        if (!cur) return cur;
        return produce(cur, (draft) => {
            if (draft.dossier[dossierId]) {
                draft.dossier[dossierId].contract = {
                    state: contract ? 'issued' : 'not-issued',
                };
            }
        });
    });

    queryClient.setQueryData<SerializedDossiers>(getArchivedDossierOverviewsQueryKey(), (cur) => {
        if (!cur) return cur;
        return produce(cur, (draft) => {
            if (draft.dossier[dossierId]) {
                draft.dossier[dossierId].contract = {
                    state: contract ? 'issued' : 'not-issued',
                };
            }
        });
    });
};

/**
 * Applies a task change to the dossier cache optimistically.
 * When taskNote is omitted, note is set to null (not left unchanged).
 */
export function applyTaskChangeToCache(
    data: SerializedDossier,
    dossierId: DossierId,
    { taskDefinition, taskNote }: { taskDefinition: TaskDefinition; taskNote?: TaskNote },
): SerializedDossier {
    return produce(data, (draft) => {
        if (draft.dossier[dossierId]?.task) {
            draft.dossier[dossierId].task.definition = taskDefinition;
            draft.dossier[dossierId].task.note = taskNote ?? null;
        }
    });
}

/**
 * Function that must be called each time a dossier is updated.
 * It allows to re-fetch the updated dossier to refresh both cache:
 * - cache of the currently updated dossier ['dossier', dossierId] (Dossier page)
 * - global dossiers list cache [folder-overview] (Homepage, Pipeline and To Do pages)
 */
export async function refreshDossierAndPropagateToOverviews(dossierId: DossierId) {
    // 1) Get server truth (automatically updates dossier cache)
    // Cancel any ongoing queries for this dossier
    await queryClient.refetchQueries({ queryKey: getDossierQueryKey(dossierId) });

    const fresh = queryClient.getQueryData<SerializedDossier>(getDossierQueryKey(dossierId));
    if (!fresh) return;

    // 2) Propagate refreshed dossier to dossier-overview cache
    queryClient.setQueriesData(
        { queryKey: getDossierOverviewsQueryKey(), exact: false },
        (cur: SerializedDossiers | undefined) => {
            if (!cur) return cur;
            return produce(cur, (draft) => {
                draft.dossier[dossierId] = fresh.dossier[dossierId];
            });
        },
    );
}
